import i18next from 'i18next';
import { push } from 'redux-first-history';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';

import * as types from './types';
import auth from '@/apis/auth';
import { instance } from '@/config/axios';
import {
  endFetching,
  setPrefilledProcessId,
  setSelectedProcess,
  setSelectedVersion,
  startFetching,
} from '@/actions/common/commonPure';
import { customername } from '../index';
import { jwtDecode } from 'jwt-decode';
import { syncOpenFeatureContext, clearOpenFeatureContext } from '../config/openFeature';

interface GetLoginSuccessAction {
  type: typeof types.LOGIN_SUCCESS;
  token: string;
  error: boolean;
  accessTime: number;
  site_name: string;
}
interface GetLoginErrorAction {
  type: typeof types.LOGIN_ERROR;
  error: boolean;
}
interface GetLogoutSuccessAction {
  type: typeof types.LOGOUT_SUCCESS;
}

export interface TokenRefreshSuccessAction {
  type: typeof types.TOKEN_REFRESH_SUCCESS;
  token: string;
  accessTime: number;
}

export function tokenRefreshSuccess(token: string, accessTime: number): TokenRefreshSuccessAction {
  return { type: types.TOKEN_REFRESH_SUCCESS, token, accessTime };
}

export function getLoginSuccess(token: string, accessTime: number, site_name: string): GetLoginSuccessAction {
  return { type: types.LOGIN_SUCCESS, token, error: false, accessTime, site_name };
}

type ThunkResult = Promise<{ ok: true }>;

export function login(mail: string, pw: string, site_name: string, nextPath?: string) {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): ThunkResult => {
    try {
      dispatch(startFetching());

      const {
        data: { token: { access, refresh } },
      } = await auth.login(mail, pw, site_name);
      instance.defaults.headers.common.Authorization = `JWT ${access}`;
      sessionStorage.setItem('token', access);
      if (refresh) sessionStorage.setItem('refresh', refresh);

      const decodedToken: { exp: number } = jwtDecode(access);
      const tokenExpiry = decodedToken.exp * 1000;
      sessionStorage.setItem('expiring', tokenExpiry.toString());
      
      syncOpenFeatureContext();

      dispatch(getLoginSuccess(access, tokenExpiry, site_name));
      dispatch(push(nextPath || `/${customername}`));

      return { ok: true };
    } catch (err: any) {
      const status: number | undefined = err?.response?.status;
      let i18nKey = 'common:login.errorMsg';

      if (status === 401 || status === 403) {
        i18nKey = 'common:login.invalidCredentials';
      } else if (status === 422) {
        i18nKey = 'common:login.invalidEmailAddress';
      } else if (status && status >= 500) {
        i18nKey = 'common:login.server';
      }

      err.__uiMessage = i18next.t(i18nKey);
      dispatch({ type: types.LOGIN_ERROR, error: true });
      try { delete instance.defaults.headers.common.Authorization; } catch {}
      throw err;
    } finally {
      dispatch(endFetching());
    }
  };
}

export function getLoginError(): GetLoginErrorAction {
  return { type: types.LOGIN_ERROR, error: true };
}

export function logout() {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    await auth.logout();
    try { delete instance.defaults.headers.common.Authorization; } catch {}
    clearOpenFeatureContext();
    dispatch(setPrefilledProcessId(''));
    // core is persisted — a stale selection would fight the URL after re-login.
    dispatch(setSelectedProcess(null));
    dispatch(setSelectedVersion(null));
    dispatch(push('/' + customername + 'login'));
    dispatch(getLogoutSuccess());
  };
}

export function getLogoutSuccess(): GetLogoutSuccessAction {
  return { type: types.LOGOUT_SUCCESS };
}
